import { getCenterSearchRadiusByLocationTypeInMeters } from '@/domain/useCases/getCenterSearchRadiusByLocationType'
import { isNonEmptyArray, isNonEmptyString, isNumber } from '@/utils/checks'
import type { Center } from '@/types/models'
import { LOCATION_TYPE } from '@/domain/location/constants'
import { PrimaryFocus } from '@/domain/primaryFocus/constant'

export type NearbyConfigObject = {
  hitsPerPage: number
  getRankingInfo: boolean
  aroundLatLng: string
  aroundRadius: number
  filters: string
  facetFilters?: string[]
}

const LOCATION_TYPES_TO_INCLUDE = [LOCATION_TYPE.REGION, LOCATION_TYPE.COUNTRY]

const LOCATION_SUD_FACET = `what_we_treat:Drug Addiction`

const LOCATION_SUD_PRIMARY_FOCUS = [
  PrimaryFocus.SUBSTANCE_USE,
  PrimaryFocus.SUBSTANCE_USE_AND_MENTAL_HEALTH,
  PrimaryFocus.BEHAVIORAL_ADDITION,
  PrimaryFocus.WELLNESS,
  PrimaryFocus.MENTAL_HEALTH
]

const primaryFocusFilterArray = LOCATION_SUD_PRIMARY_FOCUS.map(
  (item) => `primaryFocus:${item}`
)

export const createNearbyCenterConfig = ({
  lat,
  lng,
  title,
  parentLocation,
  locationType,
  hitsPerPage,
  isPageTypeFacet,
  facet,
  condition
}: {
  lat: string
  lng: string
  title?: string
  parentLocation: string
  locationType: (typeof LOCATION_TYPE)[keyof typeof LOCATION_TYPE]
  hitsPerPage: number
  isPageTypeFacet: boolean
  facet?: string
  condition?: string
}): NearbyConfigObject | undefined => {
  const algoliaSearchConfig = new Map()
  if (!isLatLngValid({ lat, lng })) return

  algoliaSearchConfig.set('hitsPerPage', hitsPerPage)
  algoliaSearchConfig.set('getRankingInfo', true)
  algoliaSearchConfig.set('aroundLatLng', `${lat}, ${lng}`)
  algoliaSearchConfig.set(
    'aroundRadius',
    getCenterSearchRadiusByLocationTypeInMeters(locationType)
  )
  algoliaSearchConfig.set(
    'filters',
    `NOT status:"Closed" AND NOT locations:"${title}"`
  )

  const facetFilters: string[] & string[][] = []

  if (isPageTypeFacet) {
    facetFilters.push(`${facet}:${condition}`)
  }
  if (!isPageTypeFacet) {
    facetFilters.push(LOCATION_SUD_FACET)
    facetFilters.push(primaryFocusFilterArray)
  }

  if (
    isNonEmptyString(parentLocation) &&
    isNonEmptyString(locationType) &&
    LOCATION_TYPES_TO_INCLUDE.includes(locationType)
  ) {
    facetFilters.push(`locations:${parentLocation}`)
  }

  if (isNonEmptyArray(facetFilters.filter(Boolean))) {
    algoliaSearchConfig.set('facetFilters', facetFilters)
  }
  const config = Object.fromEntries(algoliaSearchConfig)
  return config
}

export const isLatLngValid = ({
  lat,
  lng
}: {
  lat: unknown
  lng: unknown
}): boolean => {
  if (
    (isNonEmptyString(lat) && isNonEmptyString(lng)) ||
    (isNumber(lat) && isNumber(lng) && !isNaN(lat) && !isNaN(lng))
  ) {
    return true
  }
  return false
}
